import React from 'react';
import { Card } from '../ui/Card';
import { TrialModal } from '../modals/TrialModal';
import { LegalModal } from '../modals/LegalModal';
import { useModal } from '../../context/ModalContext';
import { useAuth } from '../../context/AuthContext';
import { useStateContext } from '../../context/StateContext';
import { unitLabels, kgToLbs, lbsToKg } from '../../utils/weight';
import { sb } from '../../modules/state';

const countries = [
  { code: 'ZA', name: 'South Africa 🇿🇦' },
  { code: 'US', name: 'United States 🇺🇸' },
  { code: 'GB', name: 'United Kingdom 🇬🇧' },
  { code: 'AU', name: 'Australia 🇦🇺' },
  { code: 'CA', name: 'Canada 🇨🇦' },
  { code: 'NZ', name: 'New Zealand 🇳🇿' },
  { code: 'IE', name: 'Ireland 🇮🇪' },
  { code: 'OTHER', name: 'Other' },
];

export function ProfileTab() {
  const { closeModal, isOpen } = useModal();

  const handleSubscribe = () => {
    closeModal();
    console.log('Subscribe triggered');
  };

  return (
    <>
      <div className="space-y-4 p-4">
        <ProfileSection />
      </div>

      <TrialModal
        isOpen={isOpen('trial')}
        onClose={closeModal}
        onSubscribe={handleSubscribe}
      />
      <LegalModal
        isOpen={isOpen('legal')}
        onClose={closeModal}
        type="privacy"
      />
    </>
  );
}

function ProfileSection() {
  const { closeModal } = useModal();
  const { currentUser, signOut } = useAuth();
  const { setProfile } = useStateContext();
  const [isSaving, setIsSaving] = React.useState(false);
  const [otherCountry, setOtherCountry] = React.useState('');
  const [localProfile, setLocalProfile] = React.useState({
    name: '',
    lastName: '',
    birthdate: '',
    sex: 'male' as 'male' | 'female',
    country: 'ZA',
    email: '',
    doctor_name: '',
    doctor_email: '',
    preferred_weight_unit: 'kg' as 'kg' | 'lbs',
    preferred_measure_unit: 'cm' as 'cm' | 'in',
    startWeight: null as number | null,
    goalWeight: null as number | null,
  });
  const [isLocked, setIsLocked] = React.useState(false);
  const [startWeightDisplay, setStartWeightDisplay] = React.useState('');
  const [goalWeightDisplay, setGoalWeightDisplay] = React.useState('');

  // Fetch profile from Supabase on mount
  React.useEffect(() => {
    const fetchProfile = async () => {
      if (!currentUser?.id) return;

      try {
        const { data, error } = await sb
          .from('profiles')
          .select('*')
          .eq('id', currentUser.id)
          .single();

        if (error) {
          console.error('[Profile] Error fetching profile:', error);
          return;
        }

        if (data) {
          // Check if profile has values that should lock fields
          const hasName = Boolean(data.name && data.name.trim());
          const hasLastName = Boolean(data.last_name && data.last_name.trim());
          const hasBirthdate = Boolean(data.birthdate);
          const hasSex = Boolean(data.sex);
          const hasEmail = Boolean(data.email);

          const profileFieldsFilled = hasName && hasLastName && hasBirthdate && hasSex && hasEmail;

          setIsLocked(profileFieldsFilled);

          // Update display states for weight inputs
          setStartWeightDisplay(data.start_weight_kg ? (data.preferred_weight_unit === 'lbs' ? kgToLbs(data.start_weight_kg).toFixed(1) : data.start_weight_kg.toString()) : '');
          setGoalWeightDisplay(data.goal_weight_kg ? (data.preferred_weight_unit === 'lbs' ? kgToLbs(data.goal_weight_kg).toFixed(1) : data.goal_weight_kg.toString()) : '');

          setLocalProfile({
            name: data.name || '',
            lastName: data.last_name || '',
            birthdate: data.birthdate || '',
            sex: (data.sex as 'male' | 'female') || 'male',
            country: data.country || 'ZA',
            email: data.email || '',
            doctor_name: data.doctor_name || '',
            doctor_email: data.doctor_email || '',
            preferred_weight_unit: (data.preferred_weight_unit as 'kg' | 'lbs') || 'kg',
            preferred_measure_unit: (data.preferred_measure_unit as 'cm' | 'in') || 'cm',
            startWeight: data.start_weight_kg,
            goalWeight: data.goal_weight_kg,
          });

          // Update StateContext with fetched profile
          setProfile({
            name: data.name || '',
            last_name: data.last_name || '',
            birthdate: data.birthdate || '',
            sex: data.sex as 'male' | 'female' || 'male',
            country: data.country || 'ZA',
            email: data.email || '',
            doctor_name: data.doctor_name || '',
            doctor_email: data.doctor_email || '',
            preferred_weight_unit: (data.preferred_weight_unit as 'kg' | 'lbs') || 'kg',
            preferred_measure_unit: (data.preferred_measure_unit as 'cm' | 'in') || 'cm',
            start_weight_kg: data.start_weight_kg,
            goal_weight_kg: data.goal_weight_kg,
          });
        }
      } catch (error) {
        console.error('[Profile] Error loading profile:', error);
      }
    };

    fetchProfile();
  }, [currentUser?.id, setProfile]);

  const handleChange = (field: string, value: string) => {
    setLocalProfile((prev) => {
      // Handle startWeight and goalWeight as numbers
      if (field === 'startWeight' || field === 'goalWeight') {
        return { ...prev, [field]: value ? parseFloat(value) : null };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleSave = async () => {
    if (!currentUser?.id) {
      console.error('User not authenticated');
      return;
    }

    setIsSaving(true);
    try {
      await sb.from('profiles').upsert({
        id: currentUser.id,
        email: localProfile.email,
        name: localProfile.name,
        last_name: localProfile.lastName,
        birthdate: localProfile.birthdate,
        sex: localProfile.sex,
        country: localProfile.country === 'OTHER' ? otherCountry : localProfile.country,
        doctor_name: localProfile.doctor_name,
        doctor_email: localProfile.doctor_email,
        preferred_weight_unit: localProfile.preferred_weight_unit,
        preferred_measure_unit: localProfile.preferred_measure_unit,
        start_weight_kg: localProfile.startWeight,
        goal_weight_kg: localProfile.goalWeight,
        updated_at: new Date().toISOString()
      }, { onConflict: 'id' });

      // Check if we now have all required fields to lock
      const hasName = Boolean(localProfile.name && localProfile.name.trim());
      const hasLastName = Boolean(localProfile.lastName && localProfile.lastName.trim());
      const hasBirthdate = Boolean(localProfile.birthdate);
      const hasSex = Boolean(localProfile.sex);
      const hasEmail = Boolean(localProfile.email);

      if (hasName && hasLastName && hasBirthdate && hasSex && hasEmail) {
        setIsLocked(true);
      }

      console.log('Profile saved successfully');
    } catch (error) {
      console.error('Error saving profile:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const saveUnitPreference = async (field: 'preferred_weight_unit' | 'preferred_measure_unit', value: string) => {
    if (!currentUser?.id) return;
    const { error } = await sb.from('profiles').upsert({
      id: currentUser.id,
      [field]: value,
      updated_at: new Date().toISOString()
    }, { onConflict: 'id' });
    if (error) console.error('[Profile] saveUnitPreference error:', error);
    setProfile({ [field]: value });
  };

  const handleLogout = async () => {
    console.log('[ProfileTab] handleLogout called, currentUser:', currentUser?.id);
    await signOut();
    console.log('[ProfileTab] SignOut complete, reloading');
    window.location.reload();
  };

  const LockedFieldLabel = ({ children }: { children: React.ReactNode }) => (
    <div className="flex items-center gap-2 mb-1">
      <span className="material-symbols-outlined text-xs text-primary">lock</span>
      <label className="block text-sm font-medium text-on-surface-variant">{children}</label>
    </div>
  );

  return (
    <div className="space-y-4">
      <Card>
        <h3 className="mb-4 text-lg font-semibold">Your Profile</h3>

        <div className="space-y-3">
          {/* Name - locked if has value */}
          <div>
            {isLocked ? (
              <LockedFieldLabel>Name</LockedFieldLabel>
            ) : (
              <label className="mb-1 block text-sm font-medium text-on-surface-variant">Name</label>
            )}
            {isLocked ? (
              <div className="w-full rounded-lg border border-outline bg-surface px-4 py-2 text-on-surface-variant">
                {localProfile.name || '-'}
              </div>
            ) : (
              <input
                type="text"
                value={localProfile.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className="w-full rounded-lg border border-outline bg-surface px-4 py-2"
              />
            )}
          </div>

          {/* Last Name - locked if has value */}
          <div>
            {isLocked ? (
              <LockedFieldLabel>Last Name</LockedFieldLabel>
            ) : (
              <label className="mb-1 block text-sm font-medium text-on-surface-variant">Last Name</label>
            )}
            {isLocked ? (
              <div className="w-full rounded-lg border border-outline bg-surface px-4 py-2 text-on-surface-variant">
                {localProfile.lastName || '-'}
              </div>
            ) : (
              <input
                type="text"
                value={localProfile.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                className="w-full rounded-lg border border-outline bg-surface px-4 py-2"
              />
            )}
          </div>

          {/* Email - locked */}
          <div>
            <LockedFieldLabel>Email</LockedFieldLabel>
            <div className="w-full rounded-lg border border-outline bg-surface px-4 py-2 text-on-surface-variant">
              {currentUser?.email || '-'}
            </div>
          </div>

          {/* Birthdate - locked if has value */}
          <div>
            {isLocked ? (
              <LockedFieldLabel>Birthdate</LockedFieldLabel>
            ) : (
              <label className="mb-1 block text-sm font-medium text-on-surface-variant">Birthdate</label>
            )}
            {isLocked ? (
              <div className="w-full rounded-lg border border-outline bg-surface px-4 py-2 text-on-surface-variant">
                {localProfile.birthdate || '-'}
              </div>
            ) : (
              <input
                type="date"
                value={localProfile.birthdate}
                onChange={(e) => handleChange('birthdate', e.target.value)}
                className="w-full rounded-lg border border-outline bg-surface px-4 py-2"
              />
            )}
          </div>

          {/* Biological Sex - locked if has value */}
          <div>
            {isLocked ? (
              <LockedFieldLabel>Biological Sex</LockedFieldLabel>
            ) : (
              <>
                <label className="mb-1 block text-sm font-medium text-on-surface-variant">Biological Sex</label>
                <p className="mb-2 text-xs text-on-surface-variant">This affects your blood panel — nothing else.</p>
              </>
            )}
            {isLocked ? (
              <div className="w-full rounded-lg border border-outline bg-surface px-4 py-2 text-on-surface-variant capitalize">
                {localProfile.sex}
              </div>
            ) : (
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => handleChange('sex', 'male')}
                  className={`flex-1 rounded-lg py-3 text-lg font-bold transition-colors ${
                    localProfile.sex === 'male'
                      ? 'bg-primary text-primary-contrast'
                      : 'bg-surface text-on-surface hover:bg-on-surface/5'
                  }`}
                >
                  ♂ Male
                </button>
                <button
                  type="button"
                  onClick={() => handleChange('sex', 'female')}
                  className={`flex-1 rounded-lg py-3 text-lg font-bold transition-colors ${
                    localProfile.sex === 'female'
                      ? 'bg-primary text-primary-contrast'
                      : 'bg-surface text-on-surface hover:bg-on-surface/5'
                  }`}
                >
                  ♀ Female
                </button>
              </div>
            )}
          </div>

          {/* Country - always editable */}
          <div>
            <label className="mb-1 block text-sm font-medium text-on-surface-variant">Country</label>
            <select
              value={localProfile.country}
              onChange={(e) => handleChange('country', e.target.value)}
              className="w-full rounded-lg border border-outline bg-surface px-4 py-2"
            >
              {countries.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
            {localProfile.country === 'OTHER' && (
              <input
                type="text"
                placeholder="Enter your country"
                value={otherCountry}
                onChange={(e) => setOtherCountry(e.target.value)}
                className="mt-2 w-full rounded-lg border border-outline bg-surface px-4 py-2"
              />
            )}
          </div>

          {/* Doctor Name - always editable */}
          <div>
            <label className="mb-1 block text-sm font-medium text-on-surface-variant">Doctor Name</label>
            <input
              type="text"
              value={localProfile.doctor_name}
              onChange={(e) => handleChange('doctor_name', e.target.value)}
              className="w-full rounded-lg border border-outline bg-surface px-4 py-2"
            />
          </div>

          {/* Doctor Email - always editable */}
          <div>
            <label className="mb-1 block text-sm font-medium text-on-surface-variant">Doctor Email</label>
            <input
              type="email"
              value={localProfile.doctor_email}
              onChange={(e) => handleChange('doctor_email', e.target.value)}
              className="w-full rounded-lg border border-outline bg-surface px-4 py-2"
            />
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={isSaving}
          className="mt-4 w-full rounded-lg bg-primary px-4 py-2 font-medium text-primary-contrast disabled:opacity-50"
        >
          {isSaving ? 'Saving...' : 'Save Profile'}
        </button>

        {/* Weight Settings Section */}
        <div className="mt-6 pt-6 border-t border-outline">
          <h4 className="mb-4 text-base font-semibold">Weight Settings</h4>
          <p className="mb-4 text-xs text-on-surface-variant">Set your starting and target weights to track your progress.</p>
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-on-surface-variant">Start Weight ({unitLabels[localProfile.preferred_weight_unit || 'kg'].plural})</label>
              <input
                type="text"
                inputMode="decimal"
                pattern="[0-9]*\.?[0-9]*"
                placeholder="Enter start weight"
                value={startWeightDisplay}
                onChange={(e) => setStartWeightDisplay(e.target.value)}
                onBlur={(e) => {
                  const num = parseFloat(e.target.value);
                  if (!isNaN(num)) {
                    const kg = localProfile.preferred_weight_unit === 'lbs' ? lbsToKg(num) : num;
                    setLocalProfile(prev => ({ ...prev, startWeight: kg }));
                  }
                }}
                className="w-full rounded-lg border border-outline bg-surface px-4 py-2"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-on-surface-variant">Goal Weight ({unitLabels[localProfile.preferred_weight_unit || 'kg'].plural})</label>
              <input
                type="text"
                inputMode="decimal"
                pattern="[0-9]*\.?[0-9]*"
                placeholder="Enter goal weight"
                value={goalWeightDisplay}
                onChange={(e) => setGoalWeightDisplay(e.target.value)}
                onBlur={(e) => {
                  const num = parseFloat(e.target.value);
                  if (!isNaN(num)) {
                    const kg = localProfile.preferred_weight_unit === 'lbs' ? lbsToKg(num) : num;
                    setLocalProfile(prev => ({ ...prev, goalWeight: kg }));
                  }
                }}
                className="w-full rounded-lg border border-outline bg-surface px-4 py-2"
              />
            </div>
          </div>
        </div>

        {/* Unit Preferences Toggle */}
        <div className="mt-6 pt-6 border-t border-outline">
          <h4 className="mb-4 text-base font-semibold">Unit Preferences</h4>
          <div className="grid grid-cols-2 gap-4">
            {/* Weight Unit Toggle */}
            <div className="flex flex-col items-center gap-2">
              <div
                className="flex items-center w-[40px] h-[22px] bg-surface-container-highest rounded-full cursor-pointer relative"
                onClick={() => {
                  const newUnit = localProfile.preferred_weight_unit === 'kg' ? 'lbs' : 'kg';
                  setLocalProfile(prev => ({ ...prev, preferred_weight_unit: newUnit }));
                  // Update display state when unit changes
                  setStartWeightDisplay(localProfile.startWeight !== null ? (newUnit === 'lbs' ? kgToLbs(localProfile.startWeight).toFixed(1) : localProfile.startWeight.toString()) : '');
                  setGoalWeightDisplay(localProfile.goalWeight !== null ? (newUnit === 'lbs' ? kgToLbs(localProfile.goalWeight).toFixed(1) : localProfile.goalWeight.toString()) : '');
                  void saveUnitPreference('preferred_weight_unit', newUnit);
                }}
              >
                <div
                  className={`absolute top-[3px] w-[16px] h-[16px] bg-white rounded-full shadow transition-transform duration-200 ${
                    localProfile.preferred_weight_unit === 'lbs' ? 'translate-x-[18px]' : 'translate-x-0'
                  }`}
                />
              </div>
              <div className="flex justify-between w-[40px] text-xs font-bold">
                <span className={localProfile.preferred_weight_unit === 'kg' ? 'text-primary' : 'text-on-surface-variant'}>kg</span>
                <span className={localProfile.preferred_weight_unit === 'lbs' ? 'text-primary' : 'text-on-surface-variant'}>lbs</span>
              </div>
            </div>

            {/* Measurement Unit Toggle */}
            <div className="flex flex-col items-center gap-2">
              <div
                className="flex items-center w-[40px] h-[22px] bg-surface-container-highest rounded-full cursor-pointer relative"
                onClick={() => {
                  const newUnit = localProfile.preferred_measure_unit === 'cm' ? 'in' : 'cm';
                  setLocalProfile(prev => ({ ...prev, preferred_measure_unit: newUnit }));
                  void saveUnitPreference('preferred_measure_unit', newUnit);
                }}
              >
                <div
                  className={`absolute top-[3px] w-[16px] h-[16px] bg-white rounded-full shadow transition-transform duration-200 ${
                    localProfile.preferred_measure_unit === 'in' ? 'translate-x-[18px]' : 'translate-x-0'
                  }`}
                />
              </div>
              <div className="flex justify-between w-[40px] text-xs font-bold">
                <span className={localProfile.preferred_measure_unit === 'cm' ? 'text-primary' : 'text-on-surface-variant'}>cm</span>
                <span className={localProfile.preferred_measure_unit === 'in' ? 'text-primary' : 'text-on-surface-variant'}>in</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-2">
          <button
            onClick={handleLogout}
            className="w-full rounded-lg bg-error-container px-4 py-2 font-medium text-error"
          >
            Log Out
          </button>
        </div>
      </Card>

      <SubscriptionSection currentUser={currentUser} />
    </div>
  );
}

function SubscriptionSection({ currentUser }: { currentUser: any }) {
  const { openModal } = useModal();
  const { isSubscribed } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = React.useState<string | null>(null);

  // Fetch subscription_status from Supabase on mount
  React.useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      if (!currentUser?.id) return;

      try {
        const { data, error } = await sb
          .from('profiles')
          .select('subscription_status')
          .eq('id', currentUser.id)
          .single();

        if (error) {
          console.error('[Profile] Error fetching subscription status:', error);
          return;
        }

        if (data?.subscription_status) {
          setSubscriptionStatus(data.subscription_status);
        }
      } catch (error) {
        console.error('[Profile] Error loading subscription:', error);
      }
    };

    fetchSubscriptionStatus();
  }, [currentUser?.id]);

  // Determine badge label and color based on subscription status
  const badgeLabel = subscriptionStatus
    ? subscriptionStatus === 'on_trial' || subscriptionStatus === 'trial'
      ? 'Trial — 7 days free'
      : subscriptionStatus === 'active'
      ? 'Active — Full access'
      : subscriptionStatus === 'expired'
      ? 'Expired'
      : 'Free'
    : 'Free';

  const badgeColor = subscriptionStatus
    ? subscriptionStatus === 'on_trial' || subscriptionStatus === 'trial'
      ? 'bg-gold text-gold-contrast'
      : subscriptionStatus === 'active'
      ? 'bg-success text-success-contrast'
      : subscriptionStatus === 'expired'
      ? 'bg-error text-error-contrast'
      : 'bg-on-surface-variant text-on-surface-container'
    : 'bg-on-surface-variant text-on-surface-container';

  return (
    <Card>
      <h4 className="mb-3 text-base font-semibold">Subscription</h4>
      <div className="mb-4 flex items-center gap-2">
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${badgeColor}`}>
          {badgeLabel}
        </span>
      </div>
      <div className="flex flex-col gap-2">
        {isSubscribed ? (
          <button
            className="w-full rounded-lg bg-primary px-4 py-2 font-medium text-primary-contrast"
            onClick={() => {
              window.open('https://app.lemonsqueezy.com/billing', '_blank');
            }}
          >
            Manage Subscription
          </button>
        ) : (
          <button
            className="w-full rounded-lg bg-primary px-4 py-2 font-medium text-primary-contrast"
            onClick={() => {
              window.open(
                'https://222fasting.lemonsqueezy.com/checkout/buy/cc5bb239-4095-4549-87c5-70a900626c44?embed=1',
                '_blank'
              );
            }}
          >
            Start Free Trial
          </button>
        )}
        {!isSubscribed && (
          <button
            className="w-full rounded-lg bg-surface-container px-4 py-2 font-medium text-on-surface hover:bg-surface-container-hover border border-outline"
            onClick={() => {
              window.open(
                'https://222fasting.lemonsqueezy.com/checkout/buy/33bc7edb-1a1a-4492-8d33-bc2badc66ecd?embed=1',
                '_blank'
              );
            }}
          >
            Subscribe · Monthly or Yearly
          </button>
        )}
        <button
          className="w-full rounded-lg bg-surface px-4 py-2 font-medium text-on-surface hover:bg-on-surface/10"
          onClick={() => openModal('legal')}
        >
          View Privacy Policy
        </button>
      </div>
    </Card>
  );
}
